import { BaseAPIService } from '../../../../../services/baseapiservice';

// ULB Type Definitions
export interface ULB {
  ulb_id: number;
  ulb_nm: string;
  ulb_te_nm?: string;
  ulb_cd?: string;
  dstrt_id?: number;
  dstrt_nm?: string;
  dstrt_te_nm?: string;
  ulb_rgn_nm?: string;
  ulb_grde_nm?: string;
  ulb_typ_id?: number;
  ulb_cnstttn_dt?: string;
  ulb_mxbl_lat?: number;
  ulb_mnbl_lat?: number;
  ulb_mxbl_lng?: number;
  ulb_mnbl_lng?: number;
  lat?: number;
  lng?: number;
  bnk_acct_nm?: string;
  bnk_acct_no?: string;
  bnk_acct_ifsc?: string;
  bnk_acct_typ?: string;
  bnk_br_nm?: string;
  bnk_nm?: string;
  a_in: number;
  logo_flstr_id?: string;
  crte_usr_id?: number;
  updte_usr_id?: number;
  i_ts?: string;
  u_ts?: string;
  created_by_name?: string;
  updated_by_name?: string;
}

export interface ULBListItem {
  ulb_id: number;
  ulb_nm: string;
  ulb_te_nm?: string;
  ulb_cd?: string;
  dstrt_id?: number;
}

// Tenant interface for new endpoint
export interface Tenant {
  id: number;
  code: string;
  name: string;
  description?: string;
  domainurl?: string;
  logoid?: string;
  imageid?: string;
  type: string;
  ulb_id?: number;
  dstrt_id?: number;
  twitterurl?: string;
  facebookurl?: string;
  emailid?: string;
  address?: string;
  contactnumber?: string;
  helplinenumber?: string;
  createddate?: string;
  lastmodifieddate?: string;
}

export interface ULBFilters {
  ulb_nm?: string;
  ulb_cd?: string;
  dstrt_id?: number;
  ulb_rgn_nm?: string;
  ulb_grde_nm?: string;
  ulb_typ_id?: number;
  a_in?: number;
}

export interface CreateULBData {
  ulb_id: number;
  ulb_nm: string;
  ulb_te_nm?: string;
  ulb_cd?: string;
  dstrt_id?: number;
  ulb_rgn_nm?: string;
  ulb_grde_nm?: string;
  ulb_typ_id?: number;
  ulb_cnstttn_dt?: string;
  ulb_mxbl_lat?: number;
  ulb_mnbl_lat?: number;
  ulb_mxbl_lng?: number;
  ulb_mnbl_lng?: number;
  lat?: number;
  lng?: number;
  bnk_acct_nm?: string;
  bnk_acct_no?: string;
  bnk_acct_ifsc?: string;
  bnk_acct_typ?: string;
  bnk_br_nm?: string;
  bnk_nm?: string;
  a_in?: number;
  logo_flstr_id?: string;
}

export interface UpdateULBData {
  ulb_nm?: string;
  ulb_te_nm?: string;
  ulb_cd?: string;
  dstrt_id?: number;
  ulb_rgn_nm?: string;
  ulb_grde_nm?: string;
  ulb_typ_id?: number;
  ulb_cnstttn_dt?: string;
  ulb_mxbl_lat?: number;
  ulb_mnbl_lat?: number;
  ulb_mxbl_lng?: number;
  ulb_mnbl_lng?: number;
  lat?: number;
  lng?: number;
  bnk_acct_nm?: string;
  bnk_acct_no?: string;
  bnk_acct_ifsc?: string;
  bnk_acct_typ?: string;
  bnk_br_nm?: string;
  bnk_nm?: string;
  a_in?: number;
  logo_flstr_id?: string;
}

class ULBService extends BaseAPIService {
  private basePath = '/core/admin/ulbs';
  private tenantPath = '/erp-admin/tenant';

  /**
   * Get all ULBs
   */
  async getAllULBs() {
    const response = await this.get(`${this.basePath}`);
    return response.data;
  }

  /**
   * Get all active ULBs (a_in = 1). The ULB master (microservice.adm_ulb_lst_t via
   * /ulbs/list) is the source of truth for district linkage — the tenant table keeps
   * dstrt_id NULL on most rows, so using it as the primary source breaks the
   * district-based dropdown filter. Tenant endpoint is kept as a secondary fallback
   * for deployments that don't expose /ulbs/list.
   */
  async getActiveULBs() {
    try {
      const listResp = await this.get(`${this.basePath}/list`);
      const rows = listResp.data?.data ?? listResp.data ?? [];
      if (Array.isArray(rows) && rows.length > 0) {
        return { status: 'success', data: rows };
      }
    } catch (error) {
      console.warn('ULB master /list unavailable, trying tenant endpoint:', error);
    }

    try {
      const response = await this.get(`${this.tenantPath}`);
      if ((response.data?.status === 200 || response.data?.status === 'success') && Array.isArray(response.data?.data)) {
        const transformedData = response.data.data.map((tenant: any) => ({
          ulb_id: tenant.ulb_id ?? tenant.id,
          ulb_nm: tenant.name,
          ulb_cd: tenant.code,
          dstrt_id: tenant.dstrt_id,
        }));
        return { status: 'success', data: transformedData };
      }
      return response.data;
    } catch (error) {
      console.error('Both ULB sources failed:', error);
      return { status: 'error', data: [] };
    }
  }

  /**
   * Get minimal ULB list for dropdowns
   */
  async getULBsList() {
    const response = await this.get(`${this.basePath}/list`);
    return response.data;
  }

  /**
   * Get all UDAs (Urban Development Authorities) from ds_dtcp.ds_uda_lst_t.
   * Each row carries { uda_id, uda_cd, uda_nm, tenant_code, tenant_id }. The numeric
   * tenant_id is what /erp-auth/select-tenant expects to switch context.
   * Used by the header UDA selector shown to State/Regional users.
   */
  async getAllUDAs(): Promise<any[]> {
    try {
      const response = await this.get('/erp-admin/uda');
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching UDAs:', error);
      return [];
    }
  }

  /**
   * Get all tenants from new tenant master table
   */
  async getAllTenants() {
    try {
      const response = await this.get(`${this.tenantPath}`);

      // Check for numeric status 200 or string 'success'
      if ((response.data?.status === 200 || response.data?.status === 'success') && response.data?.data) {
        return {
          status: 'success',
          data: response.data.data
        };
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      throw error;
    }
  }

  /**
   * Get tenant by ID
   */
  async getTenantById(id: number) {
    try {
      const response = await this.get(`${this.tenantPath}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant by ID:', error);
      throw error;
    }
  }

  /**
   * Search tenants by name
   */
  async searchTenants(name: string) {
    try {
      const response = await this.get(`${this.tenantPath}/search?name=${encodeURIComponent(name)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching tenants:', error);
      throw error;
    }
  }

  /**
   * Get tenants by district
   */
  async getTenantsByDistrict(districtId: number) {
    try {
      const response = await this.get(`${this.tenantPath}/district/${districtId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenants by district:', error);
      throw error;
    }
  }

  /**
   * Get ULB by ID
   */
  async getULBById(id: number) {
    const response = await this.get(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Get ULBs by district ID
   */
  async getULBsByDistrict(districtId: number) {
    const response = await this.get(`${this.basePath}/district/${districtId}`);
    return response.data;
  }

  /**
   * Create new ULB
   */
  async createULB(ulbData: CreateULBData) {
    const response = await this.post(`${this.basePath}`, ulbData);
    return response.data;
  }

  /**
   * Update existing ULB
   */
  async updateULB(id: number, ulbData: UpdateULBData) {
    const response = await this.put(`${this.basePath}/${id}`, ulbData);
    return response.data;
  }

  /**
   * Delete ULB (soft delete - sets a_in = 0)
   */
  async deleteULB(id: number) {
    const response = await this.delete(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Search ULBs with criteria
   */
  async searchULBs(criteria: ULBFilters) {
    const response = await this.post(`${this.basePath}/search`, criteria);
    return response.data;
  }
}

// Create and export a singleton instance
const ulbService = new ULBService();
export default ulbService;
